import * as d3 from 'd3'

import type { BidLadderChartProps, BidLevel, ChartDimensions, ChartScales } from '@/features/bidding/types/bidLadder'

function isVisible(visibilityState: Map<string, boolean> | undefined, key: string): boolean {
  return !visibilityState || visibilityState.get(key) !== false
}

function collectForecastPrices(
  primaryForecastData: BidLadderChartProps['primaryForecastData'],
  referenceForecastData: BidLadderChartProps['referenceForecastData'],
  visibilityState: Map<string, boolean> | undefined,
): number[] {
  const prices: number[] = []

  if (primaryForecastData && isVisible(visibilityState, 'Clearing Range Up')) {
    prices.push(primaryForecastData.p80Low, primaryForecastData.p80High, primaryForecastData.meanPrice)
  }

  if (referenceForecastData && isVisible(visibilityState, 'Clearing Range Down')) {
    prices.push(referenceForecastData.p80Low, referenceForecastData.p80High, referenceForecastData.meanPrice)
  }

  return prices
}

function collectOpportunityCostPrice(
  opportunityCost: BidLadderChartProps['opportunityCost'],
  visibilityState: Map<string, boolean> | undefined,
): number[] {
  if (opportunityCost === undefined) {
    return []
  }

  // Only include if visible (or no visibility state provided)
  if (!visibilityState || visibilityState.get('Opportunity Cost') !== false) {
    return [opportunityCost]
  }

  return []
}

function collectBidLevelPrices(bidLevels: BidLevel[]): number[] {
  const prices: number[] = []

  for (const bidLevel of bidLevels) {
    if (bidLevel.pricing.type === 'fixed') {
      prices.push(bidLevel.pricing.fixedPrice)
    } else if (bidLevel.pricing.type === 'range') {
      prices.push(bidLevel.pricing.minPrice, bidLevel.pricing.maxPrice)
    } else if (bidLevel.pricing.type === 'forecast') {
      prices.push(bidLevel.pricing.avgPrice)
    }
  }

  return prices
}

function calculateMaxPrice(allPrices: number[]): number {
  const DEFAULT_MAX_PRICE = 100
  const PRICE_BUFFER_MULTIPLIER = 1.05
  const ROUNDING_MULTIPLE = 1

  const maxDataPrice = allPrices.length > 0 ? Math.max(...allPrices) : DEFAULT_MAX_PRICE
  const bufferedPrice = maxDataPrice * PRICE_BUFFER_MULTIPLIER
  return Math.ceil(bufferedPrice / ROUNDING_MULTIPLE) * ROUNDING_MULTIPLE
}

function calculateMaxVolume(
  bidLevels: BidLevel[],
  totalMWAvailable: number | undefined,
): { maxUp: number; maxDown: number } {
  const DEFAULT_MAX_MW = 10
  const VOLUME_BUFFER_MULTIPLIER = 1.05

  const defaultMaxMW = totalMWAvailable || DEFAULT_MAX_MW

  let maxUpVolume = 0
  let maxDownVolume = 0

  if (bidLevels.length > 0) {
    // Separate bid levels by market direction
    for (const bidLevel of bidLevels) {
      if (bidLevel.market === 'up') {
        maxUpVolume = Math.max(maxUpVolume, bidLevel.cumulativeVolumeMW)
      } else if (bidLevel.market === 'down') {
        maxDownVolume = Math.max(maxDownVolume, bidLevel.cumulativeVolumeMW)
      }
    }

    // If no directional volumes found, use default
    if (maxUpVolume === 0 && maxDownVolume === 0) {
      maxUpVolume = defaultMaxMW
      maxDownVolume = defaultMaxMW
    }
  } else {
    maxUpVolume = defaultMaxMW
    maxDownVolume = defaultMaxMW
  }

  // Take the larger of the two directions and add 5% buffer
  const maxDirection = Math.max(maxUpVolume, maxDownVolume)
  const bufferedVolume = maxDirection * VOLUME_BUFFER_MULTIPLIER

  return {
    maxUp: bufferedVolume,
    maxDown: bufferedVolume,
  }
}

export function calculateScales(
  props: BidLadderChartProps,
  dimensions: ChartDimensions,
  visibilityState?: Map<string, boolean>,
): ChartScales {
  const { bidLevels, totalMWAvailable, primaryForecastData, referenceForecastData, opportunityCost } = props
  const { width, height, margin } = dimensions

  const chartWidth = width - margin.left - margin.right
  const chartHeight = height - margin.top - margin.bottom

  const allPrices = [
    ...collectForecastPrices(primaryForecastData, referenceForecastData, visibilityState),
    ...collectOpportunityCostPrice(opportunityCost, visibilityState),
    ...collectBidLevelPrices(bidLevels),
  ]

  const minPrice = 0
  const maxPrice = calculateMaxPrice(allPrices)
  const { maxUp, maxDown } = calculateMaxVolume(bidLevels, totalMWAvailable)

  const xScale = d3.scaleLinear().domain([-maxDown, maxUp]).range([0, chartWidth])
  const yScale = d3.scaleLinear().domain([minPrice, maxPrice]).range([chartHeight, 0])

  // max between up / down
  const maxVolume = Math.max(maxUp, maxDown)

  return { xScale, yScale, chartWidth, chartHeight, minPrice, maxPrice, maxVolume }
}
